/**
 * Pipeline quota readiness for assessment invite gating.
 *
 * Uses GET /pipelines/{id}/partner-question-quotas (config_status + bank depth).
 */
import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';

import { v1Get } from '@/lib/api';
import { IQ_ASSESSMENT_LABEL } from '@/lib/stageLabels';

const STAGE_LABELS = {
  VIDEOBOT: 'Video Bot',
  MCQ: IQ_ASSESSMENT_LABEL,
  TECH_ASSESSMENT: 'Tech Assessment',
};

const DRAFT_WARNING =
  'Pipeline is still in draft. Save question quotas in Pipeline settings before sending an invite.';

const STAGE_WARNING =
  'No {label} questions are available in this pipeline\'s role bank. Add active questions and save quotas before sending an invite.';

export function usePipelineAssessmentReadiness(pipelineId) {
  const enabled = Boolean(pipelineId);

  const quotasQuery = useQuery({
    queryKey: ['pipeline-partner-quotas', pipelineId],
    queryFn: () => v1Get(`/pipelines/${pipelineId}/partner-question-quotas`),
    enabled,
    staleTime: 60_000,
  });

  const pipeQuery = useQuery({
    queryKey: ['v1-pipeline', pipelineId],
    queryFn: () => v1Get(`/pipelines/${pipelineId}`),
    enabled,
    staleTime: 60_000,
  });

  const configStatus =
    quotasQuery.data?.config_status || pipeQuery.data?.config_status || 'draft';
  const available = quotasQuery.data?.available || {};

  const counts = useMemo(
    () => ({
      VIDEOBOT: available.VIDEOBOT?.count ?? null,
      MCQ: available.MCQ?.count ?? null,
      TECH_ASSESSMENT: available.TECH_ASSESSMENT?.count ?? null,
    }),
    [available],
  );

  const isLoading =
    enabled && (quotasQuery.isLoading || pipeQuery.isLoading);

  const isPipelineReady = configStatus === 'ready';

  const isStageReady = (stageCode) => {
    if (!enabled) return false;
    if (isLoading) return false;
    if (!isPipelineReady) return false;
    const count = counts[stageCode];
    if (count === null) return false;
    return count > 0;
  };

  const getStageWarning = (stageCode) => {
    if (!isPipelineReady) return DRAFT_WARNING;
    const count = counts[stageCode];
    if (count === null || count > 0) return null;
    const label = STAGE_LABELS[stageCode] || stageCode;
    return STAGE_WARNING.replace('{label}', label);
  };

  const getCombinedWarning = (stageCodes) => {
    if (!isPipelineReady) return DRAFT_WARNING;
    const unready = (stageCodes || []).filter((code) => !isStageReady(code));
    if (!unready.length) return null;
    if (unready.length === 1) return getStageWarning(unready[0]);
    const labels = unready.map((code) => STAGE_LABELS[code] || code);
    return `Pipeline not ready for: ${labels.join(', ')}. Save question quotas in Pipeline settings before sending an invite.`;
  };

  return {
    counts,
    isLoading,
    configStatus,
    isPipelineReady,
    isStageReady,
    getStageWarning,
    getCombinedWarning,
  };
}
